import React, {
  ReactElement,
  useEffect,
  useState,
  Fragment,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { DraggableRow } from './AccommodationCategoryPrices';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import { Entity } from '../../types/entities/Entity';
import {
  Autocomplete,
  Button,
  Button as MUIButton,
  Paper,
  Stack,
  Table,
  TableContainer,
  TextField,
  Typography,
} from '@mui/material';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { MoreOptions } from '../Table/assets/MoreOptions';
import api from '../../services/api';
import { toaster } from '../../services/toaster';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { SlidingPaneEdit } from '../Page/SlidingPaneEdit';
import { NumericFormat } from 'react-number-format';
import { debounce } from 'lodash';
import { activityWebsitePrice } from '../../schema/activityWebsitePrice';
import { SlidingPaneAddNew } from '../Page/SlidingPaneAddNew';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import CircularProgress from '@mui/material/CircularProgress';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import { formatNumber } from '../../reducer/actions/formatNumber';
import TableBody from '@mui/material/TableBody';
import { v4 as uuidv4 } from 'uuid';
import LargePopper from '../Table/assets/LargePopper';
import {LoadingButton} from "@mui/lab";
import {useStateContext} from "../../hooks/useStateContext";
import {hasPermissionByPriority} from "../../core/permissions/hasPermission";
import {ROLE_PRIORITIES} from "../../core/permissions/permissions";

type Props<T extends Entity> = {
  schema: SchemaWithClient<T>;
};

export const WebsitePrices = ({
  schema,
  values,
  tableType,
}: {
  schema: any;
  values: any;
  tableType: string;
}): ReactElement => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [properties, setProperties] = useState<any>([]);
  const [headers, setHeaders] = useState<any>([]);
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [salesPartnerOptions, setSalesPartnerOptions] = useState<any>([]);
  const [selectedSalesPartner, setSelectedSalesPartner] = useState<any>(
    {
      id: 40
    }
  );
  const [loadingButton, setLoadingButton] = useState(false);

  const [query, setQuery] = React.useState<'idle' | 'progress' | 'success'>(
    'idle'
  );
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();
  const [currentAddNewPaneEntity, setCurrentAddNewPaneEntity] = React.useState<
    any
  >({
    schema: schema,
    row: {},
    key: undefined,
  });

  const debouncedOnChange = debounce(handleDebouncedOnChange, 500);

  const tableId = useMemo(() => {
    return `table-${uuidv4()}`;
  }, [selectedSalesPartner]);

  function handleDebouncedOnChange(price: any, newValue: number | undefined) {
    submitChange(price, newValue);
  }

  const submitChange = useCallback(
    (price: any, newValue: any) => {
      setQuery('progress');

      if (newValue?.group_price) {
        const confirm = window.confirm('Split the prices?');
        if (confirm) {
          for (let i = 1; i < 51; i++) {
            const price = parseFloat((newValue?.group_price / i).toFixed(2));
            newValue[`size_${i}`] = price;
          }
        }
      }

      const tempProperties = [...properties].map((row: any) => {
        if (row?.id === price?.id) {
          Object.keys(newValue).map((column: string) => {
            row[`${column}`] = newValue[`${column}`];
          });
        }
        return row;
      });

      setProperties(tempProperties);

      activityWebsitePrice.client
        .update(price?.id, newValue)
        .then((res: any) => {})
        .finally(() => {
          timerRef.current = setTimeout(() => {
            setQuery('success');
          }, 2000);
        });
    },
    [properties]
  );

  const fetchPrices = useCallback(() => {
    const searchParams = new URLSearchParams({
      activity: values?.id,
      sales_partner: selectedSalesPartner?.id ? selectedSalesPartner.id : null,
    });
    setLoading(true);

    const prices = api
      .get('/activity-activity-prices', searchParams)
      .then((res: any) => {
        setHeaders(res.headers);
        setProperties(res.data);
        setLoading(false);
      });
  }, [selectedSalesPartner]);

  const fetchSalesPartners = useCallback(() => {
    const searchParams = new URLSearchParams({
      activity: values?.id,
    });

    const salesPartners = api
      .get('/activity-activity-prices/sales-partners', searchParams)
      .then((res: any) => {
        setSalesPartnerOptions(res);
      });
  }, []);

  const handlePaste = (event: any, index: number, price: any) => {
    event.preventDefault();
    const paste = event.clipboardData.getData('text');
    const pastedValues = paste.split('\t').slice(0, 50);
    const pastedValuesWithKey: any = {};
    pastedValues.map((item: any, pastedValueIndex: any) => {
      let pastedPrice = item.replace(' ', '');

      if (pastedPrice.includes(',') && pastedPrice.includes('.')) {
        pastedPrice = pastedPrice.replace(',', '');
      } else if (pastedPrice.includes(',')) {
        pastedPrice = pastedPrice.replace(',', '.');
      }

      pastedValuesWithKey[`size_${index + pastedValueIndex}`] = parseFloat(
        pastedPrice
      );
    });

    submitChange(price, pastedValuesWithKey);

    const defaultValues = [...properties];
    const newValues = defaultValues.map((row: any, index) => {
      if (row?.id === price?.id) {
        const updatedPrices: any = {};
        Object.keys(row).map((column: string) => {
          updatedPrices[`${column}`] = pastedValuesWithKey[column]
            ? pastedValuesWithKey[column]
            : row[column];
        });

        return updatedPrices;
      } else {
        return row;
      }
    });

    setProperties(newValues);
  };

  const propertiesRef = useRef<any>([]);

  useEffect(() => {
    propertiesRef.current = properties;
  }, [properties]);

  const moveRow = useCallback((from: number, to: number) => {
    setProperties((prev: any) => {
      const updated = [...prev];
      const [moved] = updated.splice(from, 1);
      updated.splice(to, 0, moved);
      return updated;
    });
  }, []);

  const persistOrder = useCallback(() => {
    const ids = propertiesRef.current
      .map((item: any) => item?.id)
      .filter(Boolean);

    api.post('/activity-prices/sort-order', { ids });
  }, []);

  useEffect(() => {
    fetchPrices();
  }, [values, schema, selectedSalesPartner]);

  React.useEffect(
    () => () => {
      clearTimeout(timerRef.current);
    },
    []
  );


  const {user} = useStateContext();
  const isAdmin = hasPermissionByPriority(user.roles, ROLE_PRIORITIES[1]);

  return (
    <>
      <Stack
        direction={'row'}
        alignContent={'center'}
        alignItems={'center'}
        gap={2}
        sx={{
          marginY: '20px',
          width: '100%',
        }}
      >
       {/* <Stack direction={'row'}>
          <Autocomplete
            sx={{
              '& .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline, .MuiDrawer-docked .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.2)',
              },
              width: '200px',
            }}
            size={'small'}
            PopperComponent={LargePopper}
            onOpen={() => {
              //fetchOptions();
              fetchSalesPartners();
            }}
            //defaultValue={stripperRoles?.stripper_role}
            getOptionLabel={(option: any) => option?.name}
            options={salesPartnerOptions}
            renderInput={(params) => (
              <TextField {...params} label="Sales Partner" />
            )}
            onChange={(event, newValue) => {
              console.log(newValue);
              setSelectedSalesPartner(newValue);
            }}
          />
        </Stack>*/}
        <Stack direction={'row'}>
          <Button
            disabled={!isAdmin}
            variant="contained"
            onClick={() => {
              setCurrentAddNewPaneEntity({
                row: {
                  id: 'new',
                  activity: {
                    id: values?.id,
                    name: values?.name,
                  },
                },
                schema: activityWebsitePrice,
              });
            }}
          >
            Add new
          </Button>
        </Stack>

        <Stack>
          <Box sx={{}}>
            {query === 'success' ? (
              <Stack direction={'row'} alignItems={'center'}>
                <CloudDoneRoundedIcon color={'disabled'} />
                <Typography
                  variant={'caption'}
                  marginLeft={'5px'}
                  color={'gray'}
                >
                  successfully saved
                </Typography>
              </Stack>
            ) : (
              <Fade
                in={query === 'progress'}
                style={{
                  transitionDelay: query === 'progress' ? '800ms' : '0ms',
                }}
                unmountOnExit
              >
                <CircularProgress size={20} />
              </Fade>
            )}
          </Box>
        </Stack>
      </Stack>

      <TableContainer component={Paper}>
        <Table
          aria-label="simple table"
          size="small"
          key={tableId}
          sx={{
            '& .MuiTableBody-root .MuiTableRow-root': {
              background: '#fff',
            },
            opacity: loading ? 0.5 : 1,
            pointerEvents: loading ? 'none' : 'all',
          }}
        >
          <TableHead
            sx={{
              '& .MuiTableCell-root': {
                color: 'inherit',
                border: 'none',
              },
              '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {
                border: 'none',
                fontSize: '14px',
                p: '10px',
              },
              background: '#256861',
              color: '#fff',
            }}
          >
            <TableRow
              sx={{
                '& .MuiTableCell-root': {
                  overflowWrap: 'anywhere',
                },
              }}
            >
              <TableCell
                sx={{
                  minWidth: `40px`,
                }}
              ></TableCell>
              <TableCell
                sx={{
                  minWidth: `50px`,
                }}
              ></TableCell>
              {headers.map((header: any) => {
                const width = header?.width;
                const align = header?.type === 'string' ? 'left' : 'center';
                return (
                  <TableCell
                    key={header?.id}
                    sx={{
                      minWidth: `${width}px`,
                      textAlign: `${align}`,
                    }}
                  >
                    {header?.name}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {properties.map((row: any, index: number) => {
              return (
                <DraggableRow
                  key={row?.id ? row.id : index}
                  row={row}
                  index={index}
                  headers={headers}
                  isAdmin={isAdmin}
                  dndType={'ROW-activity-website-prices'}
                  moveRow={moveRow}
                  persistOrder={persistOrder}
                  onEdit={(row: any) => {
                    setOpenedSlidingPane({
                      schema: activityWebsitePrice,
                      row: row,
                    });
                  }}
                  onDelete={(row: any) => {
                    setOpenConfirmDialog({
                      row: row,
                      schema: activityWebsitePrice,
                    });
                  }}
                  debouncedOnChange={debouncedOnChange}
                  handlePaste={handlePaste}
                  selectedSalesPartner={selectedSalesPartner}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {currentAddNewPaneEntity?.row?.id && (
        <>
          <SlidingPaneAddNew
            schema={currentAddNewPaneEntity?.schema}
            mainSchema={schema}
            id={currentAddNewPaneEntity?.row?.id}
            hasSlidingPane={false}
            onSubmit={(result: any) => {
              setCurrentAddNewPaneEntity({
                schema: schema,
                row: {},
                key: undefined,
              });

              fetchPrices();
            }}
            propertiesFromTable={
              currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}
            }
            row={
              currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}
            }
            onClose={(value) => {
              setCurrentAddNewPaneEntity({
                schema: schema,
                row: {},
                key: undefined,
              });
            }}
          />
        </>
      )}

      {openConfirmDialog && (
        <Dialog
          open={openConfirmDialog}
          onClose={() => {
            setOpenConfirmDialog(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {'Are you sure you want to delete it?'}
          </DialogTitle>
          <DialogActions>
            <MUIButton
              onClick={() => {
                setOpenConfirmDialog(false);
              }}
            >
              Cancel
            </MUIButton>
            <LoadingButton
              loading={loadingButton}
              variant={'contained'}
              color={'error'}
              onClick={() => {
                const deletedRow = openConfirmDialog?.row;
                setLoadingButton(true);
                openConfirmDialog?.schema.client
                  .delete(deletedRow.id as number, { id: deletedRow.id })
                  .then((res: any) => {
                    const tempProperties = [...properties];
                    const newRows = tempProperties.filter((item: any) => {
                      return item?.id !== deletedRow?.id;
                    });
                    setProperties(newRows);
                    toaster.show({
                      intent: 'success',
                      message: `Successfully deleted ${schema.name}: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`,
                    });
                    setOpenConfirmDialog(false);
                  })
                  .catch((e: any) => {
                    console.log(e);
                    toaster.show({
                      icon: 'error',
                      message: `Error: Unable to delete ${schema.name}: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`,
                      intent: 'danger',
                    });
                  }).finally(() => {
                  setLoadingButton(false);
                });
              }}
              autoFocus
            >
              Delete
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}

      {openedSlidingPane?.row?.id && (
        <SlidingPaneEdit
          schema={openedSlidingPane?.schema}
          id={openedSlidingPane?.row?.id}
          onSubmit={() => {
            fetchPrices();
            setOpenedSlidingPane({});
          }}
          propertiesFromTable={
            openedSlidingPane?.row ? openedSlidingPane?.row : {}
          }
          onClose={(value) => {
            setOpenedSlidingPane({});
          }}
        />
      )}
    </>
  );
};
